import { useNavigate, useParams } from "react-router-dom";
import { useGetCarAccessoryDetailsQuery } from "../../service/features/api";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { VEHICLE_CONDITION } from "../../constants/enum";
import { SaudiRiyal } from "lucide-react";

const ManageCarAccessoryDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: accessoryDetails, isLoading } = useGetCarAccessoryDetailsQuery(id, {
    skip: !id,
  });

  const accessory = accessoryDetails?.data; // Based on the user's provided JSON, the data is directly under 'data'

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (!accessory) {
    return (
      <div style={{ padding: "20px" }}>
        <p>No details found.</p>
        <button
          className="btn btn_primary"
          onClick={() => navigate("/manage-car-accessories")}
        >
          Go Back
        </button>
      </div>
    );
  }

  const sparePartInfo = [
    { label: "Accessory Name", value: accessory.name || "N/A" },
    {
      label: "Price",
      value: (
        <>
          <strong className="c_primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{<SaudiRiyal size={16} />}{accessory.discountPrice || 0}</strong>{" "}
          <del><SaudiRiyal size={16} />{accessory.actualPrice || 0}</del>
        </>
      ),
    },
    { label: "Category", value: accessory.category?.name || "N/A" },
    { label: "Sub Category", value: accessory.subCategory?.name || "N/A" },
    { label: "Brand", value: accessory.brand?.name || "N/A" },
    {
      label: "Condition",
      value: accessory.condition
        ? Object.keys(VEHICLE_CONDITION).find(key => (VEHICLE_CONDITION as any)[key] === Number(accessory.condition))?.replace(/_/g, ' ') || "N/A"
        : "N/A"
    },
    {
      label: "Small Description",
      value: accessory.shortDescription || "N/A",
    },
    {
      label: "Full Description",
      value: accessory.fullDescription || "N/A",
    },
    { label: "Created Date", value: moment(accessory.createdAt).format("DD-MMM-YYYY") },
  ];

  const compatibilityInfo = [
    { label: "Brand / Make", value: accessory.brand?.name || "N/A" },
    { label: "Model", value: accessory.model?.name || "N/A" },
    { label: "Year", value: accessory.manufacturingYear || "N/A" },
  ];

  const dealerInfo = [
    {
      label: "Dealer Name",
      value: (
        <strong
          className="anchor_link"
          onClick={() => navigate("/manage-dealers/detail")} // Adjust if dealer ID is available
        >
          {accessory.dealer?.fullName || "N/A"}
        </strong>
      ),
    },
    { label: "Phone Number", value: accessory.dealer?.phone || "N/A" },
    { label: "Email", value: accessory.dealer?.email || "N/A" },
  ];

  const images = accessory.images && accessory.images.length > 0
    ? accessory.images
    : ["/static/images/placeholder.png"];

  return (
    <>
      {/* Page Title */}
      <CommonBreadcrumb
        title="Accessory Detail"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Listed Car Accessories", onClick: () => navigate("/manage-car-accessories") },
          { label: "Accessory Detail" },
        ]}
      />

      {/* ================= Card 1: Accessory Info ================= */}
      <div className="cards view_page">
        <div className="table_header mb_20">
          <h2>Accessory Information</h2>
          <span className={`status_btn dark ${accessory.isActive ? "c_success" : "c_warning"}`}>
            {accessory.isActive ? "Active" : "Inactive"}
          </span>
        </div>

        <div className="gap_p">
          {/* Images */}
          <div className="w_100">
            <h6>Accessory Images</h6>
            <div className="gap_p">
              {images.map((img: string, index: number) => (
                <div className="w_25" key={index}>
                  <div className="vew_img no_radius v2">
                    <figure>
                      <img
                        src={img}
                        className="view_img"
                        alt={`Accessory ${index + 1}`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/static/images/placeholder.png";
                        }}
                      />
                    </figure>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accessory Details */}
          {sparePartInfo.map((item, index) => (
            <div
              className={item.label.includes("Description") ? "w_100" : "w_33"}
              key={index}
            >
              <h6>{item.label}</h6>
              <div style={{ wordBreak: "break-word" }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ================= Card 2: Vehicle Compatibility ================= */}
      <div className="cards view_page mt_2">
        <div className="table_header mb_20">
          <h2>Vehicle Compatibility</h2>
        </div>

        <div className="gap_p">
          {compatibilityInfo.map((item, index) => (
            <div className="w_33" key={index}>
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ================= Card 3: Dealer Info ================= */}
      <div className="cards view_page mt_2">
        <div className="table_header mb_20">
          <h2>Dealer Information</h2>
        </div>

        <div className="gap_p">
          {dealerInfo.map((item, index) => (
            <div className="w_33" key={index}>
              <h6>{item.label}</h6>
              <p>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ManageCarAccessoryDetail;